import React, { useEffect, useState } from 'react';
import { styles, stylesVars } from './HeroBannerCarousel.css';
import { HeroBanner, HeroBannerProps } from '../HeroBanner/HeroBanner';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, Mousewheel } from 'swiper';
import { Swiper as SwiperType } from 'swiper/types';
import 'swiper/swiper-bundle.min.css';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { PercentOffHtmlBanner, PercentOffHtmlBannerProps } from '../PercentOffHtmlBanner/PercentOffHtmlBanner';
import { ProductBannerProduct, ProductHtmlBannerProps } from '../ProductHtmlBanner/types';
import { ProductHtmlBanner } from '../ProductHtmlBanner/ProductHtmlBanner';
import { findProductById, useProducts } from '../hooks/useProducts/useProducts';
import { isExpired } from '../ProductHtmlBanner/utils';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { SupplierProductBannerProduct, SupplierProductBannerProps } from '../SupplierProductBanner/types';
import { SupplierProductBanner } from '../SupplierProductBanner';
import { ContentfulAssetFile } from '@jbhi-fi/apis/src/contentful/types';

type BaseBannerProps = {
  variant?: 'homepage' | 'collection' | 'genericPage';
  bannerTheme?: {
    background?: React.CSSProperties['color'];
    paginationActive?: React.CSSProperties['color'];
    paginationInactive?: React.CSSProperties['color'];
    arrow?: React.CSSProperties['color'];
    backgroundImage?: ContentfulAssetFile | undefined;
  };
};

type HeroBannerItemProps = BaseBannerProps &
  HeroBannerProps & {
    type: 'heroBanner';
  };

type PercentOffBannerItemProps = BaseBannerProps &
  PercentOffHtmlBannerProps & {
    type: 'percentOffBanner';
  };

type ProductBannerItemProps = BaseBannerProps &
  ProductHtmlBannerProps & {
    type: 'productBanner';
  };

type SupplierProductBannerItemProps = BaseBannerProps &
  SupplierProductBannerProps & {
    type: 'supplierBanner';
  };

type BannerItemProps =
  | HeroBannerItemProps
  | PercentOffBannerItemProps
  | ProductBannerItemProps
  | SupplierProductBannerItemProps;

const LOADING_BANNERS: Array<BannerItemProps> = Array.from(Array(2)).fill({
  isLoading: true,
  type: 'heroBanner',
});

export type HeroBannerCarouselProps = {
  variant?: 'homepage' | 'collection' | 'genericPage';
  banners: Array<BannerItemProps>;
  isLoading?: boolean;
  shouldAutoPlay?: boolean;
};

export const HeroBannerCarousel = ({
  variant = 'homepage',
  banners: bannersProp,
  shouldAutoPlay = true,
  isLoading: isLoadingProp = !bannersProp?.length,
}: HeroBannerCarouselProps) => {
  const isMobile = !useBreakpoint('md', 'up');

  const { products, isLoading: productsIsLoading } = useProducts({
    productVariantIds: bannersProp.flatMap(b =>
      b.type === 'productBanner'
        ? b.contentProducts.map(p => p.variantId)
        : b.type === 'supplierBanner'
        ? b.contentProducts.map(p => p.variantId)
        : []
    ),
  });

  const isLoading = isLoadingProp || productsIsLoading;

  const heroBanners = isLoading ? LOADING_BANNERS : bannersProp;
  const hasSingleBanner = heroBanners.length === 1;

  const [swiper, setSwiper] = useState<SwiperType>();
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(hasSingleBanner);

  const enrichedBanners = heroBanners
    .map(banner => {
      if (banner.type === 'productBanner') {
        const enrichedProducts = banner.contentProducts
          .map(product => {
            if (isExpired(product.endDate)) return null;
            const hit = findProductById(products, product.variantId.toString());
            return hit ? { ...product, hit } : null;
          })
          .filter((p): p is ProductBannerProduct & { hit: AlgoliaProductHit } => !!p);

        if (!enrichedProducts.length) {
          return null;
        }

        return {
          ...banner,
          contentProducts: enrichedProducts,
        };
      }
      if (banner.type === 'supplierBanner') {
        const enrichedProducts = banner.contentProducts
          .map(product => {
            if (isExpired(product.endDate)) return null;
            const hit = findProductById(products, product.variantId.toString());
            return hit ? { ...product, hit } : null;
          })
          .filter((p): p is SupplierProductBannerProduct & { hit: AlgoliaProductHit } => !!p);

        if (!enrichedProducts.length) {
          return null;
        }

        return {
          ...banner,
          contentProducts: enrichedProducts,
        };
      }
      return banner;
    })
    .filter(Boolean) as Array<BannerItemProps>;

  const getTheme = (): { bannerTheme: HeroBannerItemProps['bannerTheme']; navigation: boolean } => {
    if (isLoading) {
      return {
        bannerTheme: undefined,
        navigation: !hasSingleBanner && !isMobile,
      };
    }

    if (variant === 'collection') {
      return {
        bannerTheme: {
          background: palette.secondary.main,
          paginationActive: palette.primary.dark,
        },
        navigation: !isMobile,
      };
    }

    const activeBanner = enrichedBanners[activeSlideIndex];
    if (activeBanner.type === 'heroBanner' && !activeBanner?.isContained && isMobile) {
      return {
        bannerTheme: {
          background: palette.secondary.main,
          paginationActive: palette.primary.main,
        },
        navigation: !isMobile,
      };
    }

    return {
      bannerTheme: activeBanner?.bannerTheme,
      navigation: !isMobile,
    };
  };

  const { bannerTheme, navigation } = getTheme();

  useEffect(() => {
    swiper?.update();
  }, [swiper, isMobile, isLoading, hasSingleBanner]);

  useEffect(() => {
    if (!swiper) return;

    if (hasSingleBanner) {
      setIsPrevDisabled(true);
      setIsNextDisabled(true);
      return;
    }

    const isLastSlide = activeSlideIndex + 1 === enrichedBanners.length;
    const isFirstSlide = activeSlideIndex === 0;

    setIsPrevDisabled(isFirstSlide);
    setIsNextDisabled(isLastSlide);

    // eslint-disable-next-line
  }, [activeSlideIndex, hasSingleBanner, enrichedBanners.length]);

  return (
    <div
      className={styles.carouselContainer({
        template: variant,
        isPrevDisabled,
        isNextDisabled,
      })}
      data-testid="hero-banner-carousel"
    >
      <Swiper
        threshold={5}
        className={styles.carousel({ template: variant })}
        data-testid={`${variant}-hero-banner-carousel`}
        onSlideChange={swiper => {
          setActiveSlideIndex(swiper.realIndex);
        }}
        modules={[Autoplay, Navigation, Pagination, Mousewheel]}
        mousewheel={{ releaseOnEdges: true, forceToAxis: true }}
        pagination={{ clickable: true }}
        navigation={navigation}
        onSwiper={newSwiper => setSwiper(newSwiper)}
        autoplay={shouldAutoPlay ? { delay: 5000, disableOnInteraction: true } : undefined}
        loop={!hasSingleBanner}
        style={{
          zIndex: 0,
          ...assignInlineVars({
            [stylesVars.paginationActiveColor]: bannerTheme?.paginationActive ?? palette.primary.dark,
            [stylesVars.paginationInactiveColor]: bannerTheme?.paginationInactive ?? palette.border.main,
            [stylesVars.arrowColor]: bannerTheme?.arrow ?? palette.primary.dark,
          }),
        }}
      >
        {enrichedBanners.map((heroBanner, i) => (
          <SwiperSlide key={i} className={styles.slide}>
            <HeroBannerSlide
              {...heroBanner}
              bannerTheme={
                heroBanner.type === 'heroBanner' && !heroBanner.isContained && isMobile
                  ? bannerTheme
                  : heroBanner.bannerTheme
              }
              variant={variant}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

const BannerWrapper = ({
  children,
  isContained,
  variant,
  hasHorizontalPadding = false,
  bannerTheme,
  backgroundImage,
}: {
  children: React.ReactNode;
  isContained?: boolean;
  variant: 'homepage' | 'collection' | 'genericPage';
  hasHorizontalPadding?: boolean;
  bannerTheme?: BaseBannerProps['bannerTheme'];
  backgroundImage?: ContentfulAssetFile | undefined;
}) => {
  return (
    <div
      className={styles.slideContent({
        isContained: isContained ?? false,
        hasHorizontalPadding,
        template: variant,
      })}
      style={{
        backgroundColor: variant === 'collection' ? palette.secondary.main : bannerTheme?.background ?? 'transparent',
        backgroundImage: backgroundImage ? `url(${backgroundImage.url})` : 'none',
      }}
    >
      {children}
    </div>
  );
};

const HeroBannerSlide: React.FC<BannerItemProps> = ({ bannerTheme, variant = 'homepage', ...bannerProps }) => {
  switch (bannerProps.type) {
    case 'heroBanner':
      return (
        <BannerWrapper isContained={bannerProps.isContained} variant={variant} bannerTheme={bannerTheme}>
          <HeroBanner {...bannerProps} variant={variant} />
        </BannerWrapper>
      );
    case 'percentOffBanner':
      return (
        <BannerWrapper
          isContained={true}
          variant={variant}
          hasHorizontalPadding
          bannerTheme={bannerTheme}
          backgroundImage={bannerProps.backgroundImage}
        >
          <PercentOffHtmlBanner {...bannerProps} />
        </BannerWrapper>
      );
    case 'productBanner':
      return (
        <BannerWrapper
          isContained={true}
          variant={variant}
          hasHorizontalPadding
          bannerTheme={bannerTheme}
          backgroundImage={bannerProps.backgroundImage}
        >
          <ProductHtmlBanner {...bannerProps} />
        </BannerWrapper>
      );
    case 'supplierBanner':
      return (
        <BannerWrapper isContained={true} variant={variant} hasHorizontalPadding bannerTheme={bannerTheme}>
          <SupplierProductBanner {...bannerProps} />
        </BannerWrapper>
      );
    default: {
      const _exhaustive: never = bannerProps;
      return null;
    }
  }
};
